import React from 'react';
import { TFunction } from 'react-i18next';
import { object, string, array } from 'yup';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { FormConfig } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { FormActions } from '../../../DynamicComponents';
import { ValueIdentifier } from '../../../types';
import {
  creditAccountNumberValueDefiner,
  specialCaseFormFieldConfigs,
  constructSpecialFieldsValidations,
  constructSpecialFieldsPreloadedData,
} from '../../../../utils';
import { FileUpload } from '../../../../../../content-composer/src/components/Generic/FileUpload';
import { VocCode } from '../../../../domain/Transfer/entities';
import { TransferServiceImpl } from '../../../../domain/Transfer/usecases';
import { FavouriteTransfer } from '../../../../domain/FavouriteTransfer/entities';
import { ForeCastFormData } from '../../../types';
import { NO_SPECIAL_CHARS_REGEX } from '../../constants';

const internalTransferFormConfig = (
  t: TFunction,
  stepAData: { values: GenericObject },
  service: TransferServiceImpl,
  formData: Nullable<ForeCastFormData>,
  isLoading: boolean,
  vocCodes?: VocCode[],
  isLoadingVocCodes?: boolean,
  favouriteTransfer?: Nullable<FavouriteTransfer>,
): FormConfig => {
  const {
    values: { routingFormData, otherMandatoryDataForUI, routingResponseData },
  } = stepAData;

  const creditAccountNumber = creditAccountNumberValueDefiner(
    routingFormData.creditAccountNumber,
    otherMandatoryDataForUI.accounts,
    otherMandatoryDataForUI.beneficiaries,
  );

  const specialCaseFields = specialCaseFormFieldConfigs(
    t,
    routingResponseData.transferType,
    otherMandatoryDataForUI.countries,
    otherMandatoryDataForUI.currencies,
    false,
    isLoading,
    routingFormData.orderCurrency,
    routingResponseData?.bankCountry ??
      stepAData.values?.forecastFormData?.bankCountry ??
      favouriteTransfer?.bankCountrySn,
    routingResponseData?.sortCode ??
      stepAData.values?.forecastFormData?.receiverSortCode ??
      favouriteTransfer?.receiverSortCode,
    undefined,
    undefined,
    vocCodes,
    isLoadingVocCodes,
  );

  return {
    validationSchema: object(
      Object.assign(
        {
          [ValueIdentifier.DEBIT_ACCOUNT_NUMBER]: string().required(t('fundsTransferPage.Forms.Validation.EmptyField')),
          [ValueIdentifier.CREDIT_ACCOUNT_NUMBER]: string().required(
            t('fundsTransferPage.Forms.Validation.EmptyField'),
          ),
          [ValueIdentifier.ORDER_AMOUNT]: string().required(t('fundsTransferPage.Forms.Validation.EmptyField')),
          [ValueIdentifier.ORDER_CURRENCY]: string().required(t('fundsTransferPage.Forms.Validation.EmptyField')),
          [ValueIdentifier.RECEIVER_FULL_NAME]: string().required(t('fundsTransferPage.Forms.Validation.EmptyField')),
          [ValueIdentifier.RECEIVER_COMMENTS]: string()
            .required(t('fundsTransferPage.Forms.Validation.ReceiverComments'))
            .matches(NO_SPECIAL_CHARS_REGEX, t('fundsTransferPage.Forms.Validation.NoSpecialChars'))
            .max(140, t('fundsTransferPage.Forms.Validation.MaxChars')),
          [ValueIdentifier.UPLOADED_DOCS]: array(),
        },
        constructSpecialFieldsValidations(specialCaseFields, t, false),
      ),
    ),
    components: [
      {
        label: t('fundsTransferPage.Forms.FromAccount'),
        key: ValueIdentifier.DEBIT_ACCOUNT_NUMBER,
        type: 'text',
        disabled: true,
        defaultValue: '',
      },
      {
        label: t('fundsTransferPage.Forms.ToAccount'),
        key: ValueIdentifier.CREDIT_ACCOUNT_NUMBER,
        type: 'text',
        disabled: true,
        defaultValue: '',
      },
      {
        label: t('fundsTransferPage.Forms.Amount'),
        key: ValueIdentifier.ORDER_AMOUNT,
        type: 'text',
        disabled: true,
        defaultValue: '',
      },
      {
        label: t('fundsTransferPage.Forms.Currency'),
        key: ValueIdentifier.ORDER_CURRENCY,
        type: 'text',
        disabled: true,
        defaultValue: '',
      },
      {
        label: t('fundsTransferPage.Forms.BeneficiaryName'),
        key: ValueIdentifier.RECEIVER_FULL_NAME,
        type: 'text',
        disabled: true,
        defaultValue: '',
      },
      {
        label: t('fundsTransferPage.Forms.Comments'),
        key: ValueIdentifier.RECEIVER_COMMENTS,
        type: 'text',
        disabled: isLoading,
        defaultValue: '',
        properties: {
          shouldCapitalizeValue: true,
        },
      },
      ...specialCaseFields,
      {
        type: 'dynamicComponent',
        key: ValueIdentifier.UPLOADED_DOCS,
        properties: {
          component: (props: any) => (
            <FileUpload
              boxProps={{ mt: 3 }}
              description={t('fundsTransferPage.Forms.FileUploader.Description')}
              label={t('fundsTransferPage.Forms.FileUploader.Label')}
              type={t('fundsTransferPage.Forms.FileUploader.FileUploader.Type')}
              service={service}
              transactionId={routingResponseData.appTransactionId}
              shouldUploadDocuments={true}
              preselectedFileItems={
                formData?.uploadedDocs ? formData.uploadedDocs : stepAData.values?.forecastFormData?.uploadedDocs
              }
              formBuilderProps={props}
              isLoading={props.externalProps.isLoading}
              isFileItemAction={true}>
              <FormActions
                onClickCancel={props.externalEffectCallbacks?.onClickCancel}
                isLoading={props.externalProps.isLoading}
              />
            </FileUpload>
          ),
        },
      },
    ],
    preloadedData: Object.assign(
      {
        [ValueIdentifier.DEBIT_ACCOUNT_NUMBER]: otherMandatoryDataForUI.fromAccount.iban,
        [ValueIdentifier.CREDIT_ACCOUNT_NUMBER]: creditAccountNumber,
        [ValueIdentifier.ORDER_AMOUNT]: routingFormData.orderAmount,
        [ValueIdentifier.ORDER_CURRENCY]: otherMandatoryDataForUI.currency.shortDescription?.toUpperCase(),
        [ValueIdentifier.RECEIVER_FULL_NAME]: routingResponseData?.ecommbx
          ? routingResponseData.creditorFullNameUnMasked?.toUpperCase()
          : routingResponseData.creditorFullName?.toUpperCase(),
        [ValueIdentifier.RECEIVER_COMMENTS]: formData?.receiverComments
          ? formData.receiverComments?.toUpperCase()
          : stepAData.values?.forecastFormData?.receiverComments
          ? stepAData.values?.forecastFormData?.receiverComments?.toUpperCase()
          : favouriteTransfer?.receiverComments
          ? favouriteTransfer?.receiverComments?.toUpperCase()
          : '',
        [ValueIdentifier.UPLOADED_DOCS]: formData?.uploadedDocs
          ? formData.uploadedDocs
          : stepAData.values?.forecastFormData?.uploadedDocs || [],
      },
      constructSpecialFieldsPreloadedData(specialCaseFields, {
        formData,
        routingResponseData,
        forecastFormData: stepAData.values?.forecastFormData,
        favouriteTransfer,
        bopCodes: undefined,
        vocCodes,
      }),
    ),
    preloadedDataValidationOptions: { shouldValidate: false, shouldDirty: true },
  };
};

export { internalTransferFormConfig };
